import Select, { MenuListProps, SingleValue } from "react-select"
import { FixedSizeList as List } from "react-window"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { useFetchRecentSearch } from "../hooks/useFetchRecentSearch"
import { SelectOption, VesselListSingle } from "./PrevetMain"
import { useEffect, useMemo, useState } from "react"

import { useAddRecentSearch } from "../hooks/useAddRecentSearch"
import { toast } from "react-toastify"
import { AddRecentSearch } from "../hooks/usePrevetAPI"

import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"
export interface ChosenVesselForSelect {
  imo: number
}

interface PrevetVesselSearchProps {
  vesselsData: VesselListSingle[] | undefined
  getVesselsLoading: boolean
  getVesselDetailsLoading: boolean
  getChosenVessel: (args: ChosenVesselForSelect) => void
  clearChosenVesselData: () => void
  chosenVessel: SelectOption | undefined
  setChosenVessel: React.Dispatch<
    React.SetStateAction<SelectOption | undefined>
  >
  showRecentSearch: boolean
}
interface VirtualizedMenuListProps extends MenuListProps<SelectOption, false> {}

interface RenderRowProps {
  index: number
  style: React.CSSProperties
}

const VirtualizedMenuList = (props: VirtualizedMenuListProps) => {
  const { options, children, getValue } = props
  const value = getValue()
  const currentIndex = options.findIndex(
    (option) => "value" in option && option.value === value?.[0]?.value
  )
  const maxHeight = 300 // Max height of the dropdown
  const height = 35 // Height of each item in the dropdown

  const renderRow = ({ index, style }: RenderRowProps) => (
    <div style={style}>
      {children && (children as React.ReactNode[])[index]}
    </div>
  )

  return (
    <List
      height={maxHeight}
      itemCount={Array.isArray(children) ? children.length : 0}
      itemSize={height}
      initialScrollOffset={currentIndex * height}
      width="100%"
    >
      {renderRow}
    </List>
  )
}
export function PrevetVesselSearch({
  vesselsData,
  getVesselsLoading,
  getVesselDetailsLoading,
  getChosenVessel,
  clearChosenVesselData,
  chosenVessel,
  setChosenVessel,
  showRecentSearch,
}: PrevetVesselSearchProps) {
  const { postTrackingEvent } = useWoopraAPI()

  const { setTabStateValue } = usePrevetTabStateContext()
  //check recent search data
  const { recentSearchData, fetchRecentSearch, getRecentSearchLoading } =
    useFetchRecentSearch()

  //add create recent search
  const { createRecentSearch } = useAddRecentSearch()

  useEffect(() => {
    fetchRecentSearch()
  }, [fetchRecentSearch])

  const extractVesselName = (input: string): string => {
    const regex = /^(.*?)(?=\s+\S+\s*\()/
    const match = input.match(regex)
    return match ? match[0].trim() : ""
  }
  const [recentSearchDataSelect, setRecentSearchDataSelect] = useState<
    SelectOption[]
  >([])
  const [inputValue, setInputValue] = useState<string>("")

  //convert recentSearch to select options
  useEffect(() => {
    if (recentSearchData && recentSearchData.length > 0) {
      const options = recentSearchData.map((search) => ({
        value: search.vesselImo.toString(),
        label: search.vesselLabel,
      }))
      setRecentSearchDataSelect(options)
    }
  }, [recentSearchData])

  const vesselsOptions = useMemo(
    () =>
      (vesselsData ?? []).map((v) => ({
        value: String(v.value),
        label: v.label,
      })),
    [vesselsData]
  )

  const recentSearchOptions = useMemo(
    () =>
      (recentSearchData ?? []).map((s) => ({
        value: String(s.vesselImo),
        label: s.vesselLabel,
      })),
    [recentSearchData]
  )

  const vesselValueSet = useMemo(
    () => new Set(vesselsOptions.map((v) => v.value)),
    [vesselsOptions]
  )

  // recent search filtered to those that still exist in vesselsOptions
  const filteredRecent = useMemo(
    () => recentSearchOptions.filter((d) => vesselValueSet.has(d.value)),
    [recentSearchOptions, vesselValueSet]
  )

  const options = useMemo(() => {
    if (!showRecentSearch) return vesselsOptions
    if (inputValue.trim().length > 0) return vesselsOptions
    return filteredRecent.length > 0 ? filteredRecent : vesselsOptions
  }, [showRecentSearch, inputValue, filteredRecent, vesselsOptions])

  //handle submit for recent search
  const handleAddRecentSearch = async (search: AddRecentSearch) => {
    try {
      await createRecentSearch(search)
    } catch (error) {
      toast.error("Failed to add recent search", {
        position: "bottom-right",
        autoClose: 8000,
      })
    }
  }

  return (
    <>
      <Select
        components={{ MenuList: VirtualizedMenuList }}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }),
        }}
        options={
          options
          /* showRecentSearch
            ? inputValue.trim().length === 0
              ? recentSearchDataSelect.length > 0
                ? recentSearchDataSelect.filter((data)=> vesselsOptions.some(v=> String(v.value) === String(data.value)))
                : vesselsOptions
              : vesselsOptions
            : vesselsOptions */
        }
        onInputChange={(value) => setInputValue(value)}
        className="VesselSearch_Name w-100"
        isDisabled={
          getVesselDetailsLoading || getVesselsLoading || getRecentSearchLoading
        }
        value={
          chosenVessel
            ? vesselsData?.find(
                (vessel) => Number(chosenVessel.value) === Number(vessel.value)
              )
            : null
        }
        placeholder={
          getVesselsLoading
            ? `Loading vessels...`
            : "Search by Vessel Name / IMO Number"
        }
        formatOptionLabel={(option) => {
          return showRecentSearch && recentSearchDataSelect.length > 0 ? (
            inputValue.trim().length === 0 ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span className="ti-time" />
                <span>{option.label}</span>
              </div>
            ) : (
              option.label
            )
          ) : (
            option.label
          )
        }}
        onChange={(vessel: SingleValue<SelectOption>) => {
          if (!vessel) {
            return
          }
          setTabStateValue(
            "tabName",
            extractVesselName(vessel.label),
            undefined
          )
          setTabStateValue("perfExposureValue", "", undefined)
          clearChosenVesselData()
          setChosenVessel(vessel)
          getChosenVessel({
            imo: Number(vessel.value),
          })
          setTabStateValue("imo", vessel, undefined)
          postTrackingEvent({
            page: "/prevetting",
            event: "search_vessel",
            properties: {
              vesselImo: vessel.value,
              vesselName: vessel.label,
              vesselSegment: extractSegment(vessel.label),
              component: "vessel_search",
            },
          })
          handleAddRecentSearch({
            vesselImo: vessel.value.toString(),
            vesselLabel: vessel.label,
          })
        }}
      />
    </>
  )
}

type ShipSegment = "Handysize" | "Panamax" | "Capesize" | "Supramax"
function extractSegment(input: string): ShipSegment | "" {
  // Match specifically for the known segments
  const segmentMatch = input.match(/- (Handysize|Panamax|Capesize|Supramax)\)$/)
  return segmentMatch ? (segmentMatch[1] as ShipSegment) : ""
}
